<template>
  <div :class="$style.container">
    <h1 :class="$style.title">{{ title }}</h1>

    <BaseInput
      v-model="text"
      multiline
      :placeholder="$t('socialPost.placeholder')"
      :invalid="$v.text.$error"
      :error="$t('errorEmptyField')"
      @blur="$v.text.$touch()"
    />

    <div class="form-container">
      <label class="form--label">{{ $t('socialPost.image') }}</label>
      <FileInput
        v-if="!imageSrc"
        :label="$t('formAttach')"
        accept="image/*"
        @input="onFileChange"
        :class="$style.fileInput"
      >
        <BaseIcon name="paperclip" :class="$style.fileIcon" />
      </FileInput>
      <MediaPreview
        :src="imageSrc"
        @close="imageSrc = null"
        v-viewer
      />
    </div>

    <BaseSwitch
      v-model="isEnabledLikes"
      :class="$style.switchMargin"
    >{{ $t('socialPost.isEnabledLikes') }}</BaseSwitch>
    <BaseSwitch
      v-model="isEnabledComments"
    >{{ $t('socialPost.isEnabledComments') }}</BaseSwitch>

    <BaseButton
      oval
      :loading="loading"
      @click="onSubmit"
      :class="$style.button"
    >{{ buttonText }}</BaseButton>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import FileInput from '@/components/FileInput'
import imageCompression from 'browser-image-compression'
import MediaPreview from '@/components/MediaPreview'

export default {
  name: 'social-create-post',
  components: {
    FileInput,
    MediaPreview
  },
  props: {
    profileNickname: String
  },
  data () {
    return {
      text: '',
      imageFile: null,
      imageSrc: null,
      isEnabledLikes: true,
      isEnabledComments: false,
      loading: false
    }
  },
  created () {
    this.fetchProfile(this.profileNickname)
  },
  computed: {
    ...mapGetters({
      profileById: 'entities/socialProfiles/getProfile'
    }),
    profile () {
      return this.profileById(this.profileNickname)
    },
    title () {
      return this.$t('socialPost.newPostTitle')
    },
    buttonText () {
      return this.$t('buttonCreate')
    }
  },
  validations: {
    text: {
      required
    }
  },
  methods: {
    ...mapActions({
      fetchProfile: 'entities/socialProfiles/fetchProfile',
      createPost: 'entities/socialPosts/createPost'
    }),
    async onFileChange (file) {
      this.$modal.show('cropper', {
        aspectRatio: 1,
        file,
        onDone: async canvas => {
          this.imageSrc = canvas.toDataURL('image/jpeg')
          const blob = await imageCompression.getFilefromDataUrl(this.imageSrc, 'social-post-picture')
          const compressedBlob = await imageCompression(blob, {
            maxSizeMB: 1
          })
          this.imageFile = new File([compressedBlob], `${compressedBlob.name}.${compressedBlob.type.split('/')[1]}`)
        }
      })
    },
    async onSubmit () {
      this.$v.$touch()
      if (this.loading || this.$v.$invalid) return
      const data = {
        text: this.text,
        isEnabledLikes: this.isEnabledLikes,
        isEnabledComments: this.isEnabledComments
      }
      if (this.imageFile) {
        data.image = this.imageFile
      }
      this.loading = true
      try {
        await this.createPost({
          profileNickname: this.profileNickname,
          data
        })
        this.$router.push({
          name: 'social',
          params: {
            profileNickname: this.profileNickname
          }
        })
      } catch (e) {
        this.$modal.show('error', {
          text: JSON.stringify(e.response.data)
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style module lang="scss">
// .container {
//   flex-grow: 1;
//   padding: 20px;
//   overflow-x: hidden;
//   overflow-y: scroll;
// }
.container {
  padding: 16px 25px;
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: scroll;
  .button {
    margin: 14px auto;
  }
}
.title {
  font-size: 18px;
  font-weight: 500;
  color: $text-success;
  margin: 0;
  margin-bottom: 16px;
  text-align: center;
}
.file {
  margin-bottom: 14px;
}
.fileIcon {
  font-size: 18px;
  color: $text-colored;
}
.imageWrap {
  text-align: center;
}
.image {
  margin: 0 auto;
  max-width: 100%;
  max-height: 450px;
  object-fit: contain;
}
.switchMargin {
  margin-bottom: 2px;
}
</style>
